import type { CartPackageImageNodeDisplayModel } from '@shared/models/carts/cartPackageImageNodes';
import type {
  Cartv2PackageDetailsDisplayModel,
  Cartv2PackageDisplayModel,
  Cartv2PackageItemDisplayModel,
} from '@shared/models/carts/cartPackages';
import type { CartPackageTextNodeDisplayModel } from '@shared/models/carts/cartPackageTextNotes';
import type { SubjectFieldCreateModel, SubjectFieldDisplayModel } from '@shared/models/subjects/subjectFields';
import { DIGITAL_DOWNLOAD_CODES } from '@shared/models/products/productLookup';

const obscureValue = (value: string | undefined) => {
  if (!value) return '';
  const firstValue = value.replace(/(.{2})(.*)(?=@)/, (gp1, gp2, gp3) => {
    for (let i = 0; i < gp3.length; i++) {
      gp2 += '*';
    }
    return gp2;
  });

  if (firstValue !== value) {
    return firstValue;
  }
  const totalValueLength = value.length > 5 ? 5 : 0;
  return value.replace(value.substring(value.length, totalValueLength), '*****');
};
export const saveableTextNodeValue = (node: CartPackageTextNodeDisplayModel) =>
  node.obscure && node.value === obscureValue(node.originalValue) // they didn't change the obscured value
    ? node.originalValue
    : node.value;

export const populateTextNodeValue = (textNode: CartPackageTextNodeDisplayModel, value: string) => {
  textNode.value = textNode.obscure ? obscureValue(value) : value;
  textNode.originalValue = value;
};

export const getImageNodes = (item: Cartv2PackageItemDisplayModel): CartPackageImageNodeDisplayModel[] =>
  item.imageNodes;
export const getTextNodes = (item: Cartv2PackageItemDisplayModel): CartPackageTextNodeDisplayModel[] => item.textNodes;
export const getPackageTextNodes = (pkg: Cartv2PackageDisplayModel) =>
  pkg.cartPackageItems.map((i) => getTextNodes(i)).flat();
export const getPackageImageNodes = (pkg: Cartv2PackageDisplayModel) =>
  pkg.cartPackageItems.map((i) => getImageNodes(i)).flat();

export const isTextNodeComplete = (textNode: CartPackageTextNodeDisplayModel): boolean => {
  return !!textNode.value?.trim();
};

export const isDigitalDownloadProduct = (item: Cartv2PackageItemDisplayModel): boolean => {
  return DIGITAL_DOWNLOAD_CODES.some((c) => c == item.productCode);
};

export const areTextNodesComplete = (item: Cartv2PackageItemDisplayModel): boolean => {
  if (isDigitalDownloadProduct(item)) return true; // Digital download items from RS+ are always complete
  const textNodes = getTextNodes(item);
  return textNodes.every(isTextNodeComplete);
};

export const isImageNodeComplete = (imageNode: CartPackageImageNodeDisplayModel): boolean => {
  if (imageNode.subjectImageId) return true;
  if (imageNode.groupImageId) return true;

  return false;
};

export const areImageNodesComplete = (item: Cartv2PackageItemDisplayModel): boolean => {
  const imageNodes = getImageNodes(item);
  return imageNodes.every(isImageNodeComplete);
};

export const isItemComplete = (item: Cartv2PackageItemDisplayModel): boolean => {
  return areImageNodesComplete(item) && areTextNodesComplete(item);
};

/**
 * Image 3 nodes are never defaulted and should always require a user to select an image
 * @param imageNode 
 * @returns 
 * @see https://github.com/Millers-IT/tickets/issues/4540
 */
export const isImage3Node = (imageNode: CartPackageImageNodeDisplayModel): boolean => {
  return imageNode.name.toLocaleLowerCase().includes('image3');
};

const FIELD_ALIASES = {
  firstname: ['first name', 'firstname'],
  lastname: ['last name', 'lastname'],
  phonenumber: ['phone number', 'phonenumber', 'phone'],
  dateofbirth: ['date of birth', 'dateofbith', 'birthday'],
};

type subjectFields = (SubjectFieldDisplayModel | SubjectFieldCreateModel)[];

const fieldFinder = (dataFields: subjectFields, ...names: string[]) =>
  dataFields.find((f) => names.includes(f.name.toLowerCase()));

// Provides a utility function that gets subject data fields based on common field names
export const getSubjectDataField = (dataFields: subjectFields, field: string): SubjectFieldDisplayModel | null => {
  const fieldName = field.toLowerCase();
  const aliasField = Object.values(FIELD_ALIASES).find((a) => a.includes(fieldName));
  const dataField = fieldFinder(dataFields, ...(aliasField || [fieldName])) as SubjectFieldDisplayModel;
  return (
    dataField || {
      id: 0,
      name: field,
      value: '',
      hasOptions: false,
    }
  );
};

export const getSubjectDataFieldValue = (dataFields: subjectFields, field: string): string => {
  const fieldName = field.toLowerCase();
  if (fieldName === 'name') {
    const firstName = fieldFinder(dataFields, ...FIELD_ALIASES.firstname);
    const lastName = fieldFinder(dataFields, ...FIELD_ALIASES.lastname);
    return `${firstName?.value} ${lastName?.value}`;
  }
  return getSubjectDataField(dataFields, field)?.value || '';
};

export const fillTextNode = (
  node: CartPackageTextNodeDisplayModel,
  dataFields: subjectFields,
): Partial<CartPackageTextNodeDisplayModel> => {
  const fieldName = node.dataSource?.toLowerCase() || node.altDataSource?.toLowerCase() || node.name?.toLowerCase();
  if (!fieldName) {
    return {
      obscure: false,
      value: '',
      originalValue: '',
    };
  }

  const obscure = fieldName.startsWith('email') || fieldName.startsWith('phone');
  let value = getSubjectDataFieldValue(dataFields, fieldName);

  const originalValue = value;
  if (obscure) {
    value = obscureValue(value);
  }
  const autoFilled = !!value;
  return {
    obscure,
    value,
    originalValue,
    autoFilled,
  };
};

export const fillAutoFilledTextNode = (
  node: CartPackageTextNodeDisplayModel,
  dataFields: SubjectFieldDisplayModel[],
  hasImportedData: boolean,
): Partial<CartPackageTextNodeDisplayModel> => {
  const fieldName = node.dataSource?.toLowerCase() || node.altDataSource?.toLowerCase() || node.name?.toLowerCase();

  let autoFilled = false;
  if (!!fieldName && hasImportedData) {
    const value = getSubjectDataFieldValue(dataFields, fieldName);

    autoFilled = !!value;
  }

  return {
    autoFilled,
  };
};
